import { useCallback, useEffect, useRef, useState } from 'react';
import {
    ThreeDotsSvg,
    AngularLoadingSpinner,
    OnboardPauseStatus,
    OnboardCompleteStatus,
    OnboardPlanStatus,
    OnboardFileError,
    OnboardUploadedFileSvg,
    OnboardTransferSvg
} from '../Svgs';
import './AllOnboardFiles.scss';
import useFiles from '../../../../hooks/useFiles';
import ShimmerMenuList from '../../../../../CommonComponents/ShimmerEffectDesign/ShimmerMenu';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import PriorityModal from '../PriorityModal';

type AllOnboardFilesProps = {
    allOnboardFiles?: any[] | null;
    isReadOnlyActions?: boolean;
    setActionFileId: (value: { status: string | null, fileId: string | null }) => void;
    setTypeOfAction: (value: 'delete' | 'cancel') => void;
};

const AllOnboardFiles: React.FC<AllOnboardFilesProps> = ({ allOnboardFiles, isReadOnlyActions = false, setTypeOfAction, setActionFileId }) => {
    const [rowMenuOpen, setRowMenuOpen] = useState<any | null>(null)
    const menuRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const { isOnboardFilesLoading } = useFiles();

    //HANDLE THREE DOTS OPENING AND CLOSING
    useEffect(() => {
        const clickOutside = (e: MouseEvent) => {
            const allMenus = document.querySelectorAll('.three-dots-all-onboard-files');
            const priorityModal = document.querySelector('.priority_modal_selector');
            const priorityDropdown = document.querySelector('.priority_modal_dropdown')
            const menu = rowMenuOpen !== null ? menuRefs.current[rowMenuOpen?.uniqueId] : null;
            if (menu && menu.contains(e.target as Node)) return;
            if (priorityModal && priorityModal.contains(e.target as Node)) return;
            if (priorityDropdown && priorityDropdown.contains(e.target as Node)) return;

            for (let i = 0; i < allMenus.length; i++) {
                if (allMenus[i].contains(e.target as Node)) {
                    return; // Clicked inside any of the menus
                }
            }
            setRowMenuOpen(null); // Clicked outside all menus
        };

        const handleScroll = () => {
            setRowMenuOpen(null); // Close menu on component scroll
        };

        const container = containerRef.current;
        container?.addEventListener('scroll', handleScroll); // attach scroll to component
        document.addEventListener('mousedown', clickOutside);

        return () => {
            document.removeEventListener('mousedown', clickOutside);
            container?.removeEventListener('scroll', handleScroll);
        };
    }, [rowMenuOpen]);


    //HANDLE MENU OPEN
    const handleRowMenuOpen = (e: React.MouseEvent, file: any) => {
        if (isReadOnlyActions) return;
        setRowMenuOpen((prev) => {
            if (prev?.uniqueId === file?.uniqueId) { // If already open for this file → close it
                return null;
            }
            return file; // Else → open this file
        });

        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const menuHeight = 150; // approximate menu height or calculate dynamically
        const spaceBelow = window.innerHeight - rect.bottom;

        let top: number;

        if (spaceBelow < menuHeight) {
            top = rect.top + window.scrollY - menuHeight + 50;  // Not enough space below → open upward
        } else {
            top = rect.bottom + window.scrollY - 94; // Enough space → open downward
        }

        const left = rect.right - 140 + window.scrollX; // Adjust left as needed (your existing logic)
        setMenuPosition({ top, left });
    };


    //DELETE FILE FROM ONBOARD
    const handleDeleteOnboard = useCallback((e, file) => {
        e.preventDefault();
        setActionFileId({ status: file?.status, fileId: file?.uniqueId });
        setTypeOfAction('delete');
        setRowMenuOpen(null);
    }, [setActionFileId, setTypeOfAction, setRowMenuOpen]);

    //FILE STARTING MENU OPTIONS
    const renderStartingFileMenu = () => (
        <>
            <span
                role="button"
                className="three-dots-menu-options"
                onClick={(e) => handleDeleteOnboard(e, rowMenuOpen)}
            >
                <span className="ms-2">Delete Onboard</span>
            </span>

            <div className="three-dots-menu-options">
                <PriorityModal
                    side="right"
                    currentPriority={
                        rowMenuOpen?.filePriority === "FILE_DL_PRIORITY_LOW" ? "Low" :
                            rowMenuOpen?.filePriority === "FILE_DL_PRIORITY_NORMAL" ? "Default" :
                                rowMenuOpen?.filePriority === "FILE_DL_PRIORITY_HIGH" ? "High" :
                                    rowMenuOpen?.filePriority === "FILE_DL_PRIORITY_IMMEDIATE" ? "Immediate" :
                                        "Default"
                    }
                    rowMenuOpen={rowMenuOpen}
                    setRowMenuOpen={setRowMenuOpen}
                    setActionFileId={setActionFileId}
                    setTypeOfAction={setTypeOfAction}
                />
            </div>
        </>
    );

    //FILE INPROGRESS AND IN PAUSE MENU OPTIONS
    const renderInProgressMenu = () => (
        <>
            <span
                role="button"
                className="three-dots-menu-options"
                onClick={(e) => handleDeleteOnboard(e, rowMenuOpen)}
            >
                <span className="ms-2">Delete Onboard</span>
            </span>

            {/* Future options can be easily added here */}
            {/* 
            {rowMenuOpen.status === "download_inprogress" && (
                <span role="button" className="three-dots-menu-options">
                    <span className="ms-2">Pause</span>
                </span>
            )}

            {rowMenuOpen.status === "download_suspend" && (
                <span role="button" className="three-dots-menu-options">
                    <span className="ms-2">Resume</span>
                </span>
            )}
            <span
                role="button"
                className="three-dots-menu-options"
                onClick={() => handleCancel(rowMenuOpen)}
            >
                <span className='ms-2'>Cancel</span>
            </span>
            */}
        </>
    );

    const renderCompletedMenu = () => (
        <span
            role="button"
            className="three-dots-menu-options"
            onClick={(e) => handleDeleteOnboard(e, rowMenuOpen)}
        >
            <span className="ms-2">Delete Onboard</span>
        </span>
    )

    //THEREE DOTS MENU CONTENT BASED ON STATUS OF FILE
    const renderMenuContent = () => {
        if (!rowMenuOpen) return null;

        switch (rowMenuOpen.status) {
            case "download_pending":
                return renderStartingFileMenu();
            case "download_inprogress":
            case "download_suspend":
                return renderInProgressMenu();
            case 'download_success':
                return renderCompletedMenu();
            case 'download_cancel':
                return renderCompletedMenu();
            default:
                return null;
        }
    };

    return (
        <div className='all-onboard-files-wrapper ps-4'>
            <div className="all-onboard-files-table">
                {/* HEADING OF THE TABLE */}
                <div className="all-onboard-files-table-header">
                    <span className='w-35 all-onboard-files-table-header-text'><span style={{ marginLeft: '18px' }}>Name</span></span>
                    <span className='w-25 all-onboard-files-table-header-text'>Last Modified</span>
                    <span className='w-30 all-onboard-files-table-header-text'>Downlink Band</span>
                    <span className='w-10 all-onboard-files-table-header-text'>Status</span>
                </div>
                {/* BODY OF THE TABLE */}
                <div className="all-onboard-files-table-body gap-1" ref={containerRef}>
                    {!isOnboardFilesLoading ? (
                        (allOnboardFiles || []).length > 0 ? (
                            (allOnboardFiles || []).map((file) => (
                                <div key={file?.uniqueId} className="position-relative">
                                    {/* FILE ROW  */}
                                    <div className="all-onboard-files-table-body-row">
                                        <div
                                            className={`w-35 d-flex gap-2 all-onboard-files-table-row-text ${file?.status === 'download_inprogress' ? 'active' : ''}`}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            <div className="d-flex justify-content-center opacity-50">
                                                {file?.status === 'Uploaded' ? (
                                                    <OnboardUploadedFileSvg />
                                                ) : (
                                                    <span className='onboard_empty_block' />
                                                )}
                                            </div>

                                            <CustomToolTip title={file?.fileName ?? ''} placement="top">
                                                <span className="text-truncate d-block">
                                                    {file?.fileName}
                                                </span>
                                            </CustomToolTip>
                                        </div>

                                        <span className={`w-25 all-onboard-files-table-row-text-last-modified ${file?.status === 'download_inprogress' ? 'active' : ''}`}>
                                            {file?.updatedAt}
                                        </span>

                                        <span className={`w-30 all-onboard-files-table-row-text-file-size ${file?.status === 'download_inprogress' ? 'active' : ''}`}>
                                            {file?.radioId}
                                        </span>

                                        <span className="w-10 d-flex justify-content-between align-items-center all-onboard-files-table-row-text">
                                            {/* STATUS ICONS */}
                                            <span className="d-flex align-items-center">
                                                {file?.status === 'download_inprogress' ? (
                                                    <AngularLoadingSpinner />
                                                ) : file?.status === 'download_suspend' ? (
                                                    <OnboardPauseStatus />
                                                ) : file?.status === 'download_success' ? (
                                                    <OnboardCompleteStatus />
                                                ) : file?.status === 'download_pending' ? (
                                                    <OnboardPlanStatus />
                                                ) : file?.status === 'Error' ? (
                                                    <OnboardFileError />
                                                ) : file?.status === 'Uploaded' ? (
                                                    <OnboardTransferSvg />
                                                ) : (
                                                    <span className='allonboard-empty-icon' />
                                                )}
                                            </span>

                                            {/* THREE DOTS ICONS */}
                                            <span
                                                role="button"
                                                className="d-flex cursor-pointer three-dots-all-onboard-files"
                                                onClick={(e) => handleRowMenuOpen(e, file)}
                                            >
                                                {((!file?.status || file?.status === '') || (file?.status !== 'download_pending' && file?.status !== 'download_inprogress' && file?.status !== 'download_success' && file?.status !== 'download_cancel'))
                                                    ? ''
                                                    : <span className={isReadOnlyActions ? 'opacity-50' : ''}><ThreeDotsSvg /></span>}
                                            </span>
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-files-text">No files found</div>
                        )
                    ) : (
                        isOnboardFilesLoading && <ShimmerMenuList reducedRows={1} />
                    )}

                    {/* THREE DOTS MENU */}
                    <div
                        ref={(el) => {
                            if (rowMenuOpen) {
                                menuRefs.current[rowMenuOpen?.uniqueId] = el;
                            }
                        }}
                        className={`three-dots-menu-onboard-allfiles ${!isReadOnlyActions && rowMenuOpen ? "active" : ""}`}
                        style={{
                            width: "126px",
                            top: menuPosition.top,
                            left: menuPosition.left,
                        }}
                    >
                        {renderMenuContent()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default AllOnboardFiles;
